import { useCallback } from 'react';
import { create } from 'zustand';
import { useEditorStore } from '../store/editorStore';
import { useMediaLibraryStore } from '../store/mediaLibraryStore';
import { setActiveAsset } from '../renderer/rendererBridge';
import { generateProxy } from '../engine/ffmpegBridge';
import { saveProxy, saveWaveform, saveAudioBuffer } from '../engine/proxyManager';
import { extractPeaks } from '../engine/peakGenerator';
import { saveProject } from '../engine/projectStorage';

/** Lightweight store for import-in-progress state */
export const useImportingStore = create<{ importing: boolean }>(() => ({ importing: false }));

/**
 * Import a library asset into the current project.
 * Downloads R2 video → FFmpeg proxy transcode → IDB save → creates tracks/clips → inits decoder.
 */
export function useImportToProject() {
  const addAsset = useEditorStore((s) => s.addAsset);
  const addTrack = useEditorStore((s) => s.addTrack);
  const addClip = useEditorStore((s) => s.addClip);
  const setProjectAspect = useEditorStore((s) => s.setProjectAspect);

  const importAsset = useCallback(async (libraryAssetId: string) => {
    const libraryAsset = useMediaLibraryStore.getState().getAsset(libraryAssetId);
    if (!libraryAsset || libraryAsset.ingestStatus !== 'ready') return;

    // Check if already in project
    const existing = useEditorStore.getState().project.assets.find((a) => a.id === libraryAssetId);
    if (existing) return;

    useImportingStore.setState({ importing: true });

    // 1. Download original from R2
    let proxyData: ArrayBuffer;
    let hasAudio = false;
    try {
      const res = await fetch(libraryAsset.publicUrl);
      const originalData = await res.arrayBuffer();

      // 2. Try FFmpeg proxy transcode with timeout, fall back to original
      try {
        const proxyPromise = generateProxy(
          libraryAssetId,
          originalData.slice(0),
          libraryAsset.filename,
        );
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('FFmpeg timeout')), 60000),
        );
        const proxyResult = await Promise.race([proxyPromise, timeoutPromise]);
        proxyData = proxyResult.proxyData;
      } catch (ffmpegErr) {
        console.warn('FFmpeg proxy failed, using original file:', ffmpegErr);
        proxyData = originalData;
      }

      // 3. Save proxy to IDB
      await saveProxy(libraryAssetId, proxyData);

      // 4. Generate waveform + audio buffer for timeline rendering (skip if no audio track)
      try {
        const audioCtx = new AudioContext();
        const audioBuf = await audioCtx.decodeAudioData(proxyData.slice(0));
        await audioCtx.close();
        const peakData = extractPeaks(audioBuf, 256);
        await saveWaveform(libraryAssetId, peakData);
        await saveAudioBuffer(libraryAssetId, proxyData.slice(0));
        hasAudio = true;
      } catch {
        // Video has no audio track — skip waveform generation
      }
    } catch (err) {
      console.error('Failed to prepare proxy:', err);
      useImportingStore.setState({ importing: false });
      return;
    }

    // 5. Add asset to project
    addAsset({
      id: libraryAsset.id,
      filename: libraryAsset.filename,
      mimeType: libraryAsset.mimeType,
      originalUrl: libraryAsset.publicUrl,
      durationMs: libraryAsset.durationMs,
      width: libraryAsset.width,
      height: libraryAsset.height,
      fps: 30,
      ingestStatus: 'ready',
      ingestProgress: 100,
      mediaType: libraryAsset.mediaType as 'video' | 'audio',
      proxyKey: libraryAsset.r2Key,
    });

    // 6. Auto-set project aspect from first imported video
    const projectAssets = useEditorStore.getState().project.assets;
    if (projectAssets.length === 1 && libraryAsset.width > 0 && libraryAsset.height > 0) {
      setProjectAspect(libraryAsset.width / libraryAsset.height);
    }

    // 7. Create video track + clip
    const videoTrackId = addTrack('video', libraryAsset.filename);
    addClip(videoTrackId, {
      assetId: libraryAssetId,
      startMs: 0,
      durationMs: libraryAsset.durationMs,
      sourceOffsetMs: 0,
    });

    // 8. Create paired audio track + clip (only if the video has audio)
    if (hasAudio) {
      const audioTrackId = addTrack('audio', libraryAsset.filename);
      addClip(audioTrackId, {
        assetId: libraryAssetId,
        startMs: 0,
        durationMs: libraryAsset.durationMs,
        sourceOffsetMs: 0,
      });
    }

    // 9. Init decoder + audio engine (proxy is in IDB)
    try {
      await setActiveAsset(libraryAssetId);
    } catch (err) {
      // May fail for videos without audio — decoder still works for video
      console.warn('setActiveAsset partial failure (no audio track?):', err);
    }

    // 10. Auto-fit zoom
    const canvasEl = document.querySelector('canvas');
    if (canvasEl) {
      useEditorStore.getState().zoomToFit(canvasEl.getBoundingClientRect().width);
    }

    // 11. Save project with the asset's thumbnail so it shows on /studio
    if (libraryAsset.thumbnailUrl) {
      const project = useEditorStore.getState().project;
      saveProject(project, libraryAsset.thumbnailUrl).catch(console.error);
    }

    useImportingStore.setState({ importing: false });
  }, [addAsset, addTrack, addClip, setProjectAspect]);

  return { importAsset };
}
